"use client";

import { FC, useState } from "react";
import clsx from "clsx";

import LeadCaptureForm from "@/components/forms/LeadCaptureForm";
import { trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// On-page lead card for the pentest / VAPT landing pages. Sits directly under
// the hero (anchor id="lead-form") so the hero CTAs scroll to a form that is
// already on the page — the conversion never requires a navigation.
//
// A two-pill toggle switches the LeadCaptureForm between the two low-friction
// offers (quote / sample report) so both are available in one card without a
// second page.
// ----------------------------------------------------------------------------

type IInlineLeadCardProps = {
  variant?: string;
};

const InlineLeadCard: FC<IInlineLeadCardProps> = ({ variant = "pentest" }) => {
  const [intent, setIntent] = useState<"quote" | "sample-report">("quote");

  const select = (next: "quote" | "sample-report", label: string) => {
    if (next === intent) return;
    setIntent(next);
    trackEvent("cta_click", {
      cta_label: label,
      cta_location: "inline_card_toggle",
      page_variant: variant,
    });
  };

  return (
    <div
      id="lead-form"
      className="scroll-mt-28 rounded-2xl border border-black-100 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(31,42,73,0.35)] md:p-8"
    >
      {/* Offer toggle */}
      <div className="mb-5 grid grid-cols-2 gap-1 rounded-full bg-black-50 p-1">
        <button
          type="button"
          onClick={() => select("quote", "Toggle: Quote")}
          className={clsx(
            "h-10 rounded-full font-bricolage-grotesque text-sm font-medium transition-all",
            intent === "quote"
              ? "bg-white text-primary-900 shadow-sm"
              : "text-black-600 hover:text-black-900",
          )}
        >
          Get a quote
        </button>
        <button
          type="button"
          onClick={() => select("sample-report", "Toggle: Sample report")}
          className={clsx(
            "h-10 rounded-full font-bricolage-grotesque text-sm font-medium transition-all",
            intent === "sample-report"
              ? "bg-white text-primary-900 shadow-sm"
              : "text-black-600 hover:text-black-900",
          )}
        >
          Sample report
        </button>
      </div>

      <h2 className="mb-1 font-bricolage-grotesque text-h7 font-medium text-black-900 md:text-h5">
        {intent === "quote"
          ? "Get a fixed-scope pentest quote"
          : "See a real sample report"}
      </h2>
      <p className="mb-5 font-darker-grotesque text-base font-normal text-black-700">
        {intent === "quote"
          ? "A CERT-In empanelled engineer replies within one business day."
          : "Redacted findings, severity ratings, and remediation guidance."}
      </p>

      {/* key forces a fresh form (and reCAPTCHA) when the offer switches */}
      <LeadCaptureForm key={intent} intent={intent} />
    </div>
  );
};

export default InlineLeadCard;
